import { useCallback } from 'react';
import produce from 'immer';
import {
  DesignFeatureType,
  DesignLayoutType,
  EventDesignFragment,
  EventDesignPreferenceBooleanFragment,
  EventWebsiteGuestQuery,
  GetThemeByThemeIdQuery,
  useGetThemeByThemeIdQuery,
  WebsitePagePhotoPresentationCropInfoFragment,
  WebsitePagePhotoPresentationFragment
} from '@graphql/generated';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { parseColor, isLightColor } from '@shared/utils/color';
import chroma from 'chroma-js';
import { useFeatureValue } from '@shared/core/featureFlags/featureFlags.provider';
import { PhotoPresentationLayout } from '@shared/components/FocusPointEditor/FocusPointEditor.types';
import uuid from 'uuid';

type StringifiedBoolean = 'true' | 'false';

type ApplyThemeAndLayoutArgs = {
  draft: EventDesignFragment;
  selectedLayout?: DesignLayoutType;
  themeData?: GetThemeByThemeIdQuery;
  font?: string;
  textColor?: string;
  linkColor?: string;
  backgroundColor?: string;
  pageColor?: string;
  pageFontColor?: string;
  isCustomColorsEnabled: boolean;
  pageTransparency?: StringifiedBoolean;
  pageShadow?: StringifiedBoolean;
  photoPresentationLayout?: PhotoPresentationLayout;
};

/**
 * Type representing URL query parameters for theme and layout overrides
 */
type QueryParamsValues = {
  /** Layout type override (aloha or brannan) */
  layout?: DesignLayoutType;
  /** Theme ID override. If provided the theme will be fetched from the database and applied to the event design. */
  theme?: string;
  /** Font family override */
  font?: string;
  /** Text color override. This will be applied to the font of first page of aloha layout */
  tc?: string;
  /** Link color override. This will be applied to all buttons and links except the rsvp button */
  lc?: string;
  /** Background color override. This will be applied to the background of the first page of aloha layout */
  bc?: string;
  /** Page color override. This will be applied to the background of all pages */
  pc?: string;
  /** Page font color override. This will be applied to the font of all pages */
  pfc?: string;
  /** Page transparency override ('true' or 'false').These are applicable only in brannan layout */
  pt?: StringifiedBoolean;
  /** Page shadow override ('true' or 'false').These are applicable only in brannan layout */
  ps?: StringifiedBoolean;
  /** Photo presentation layout override. This will be applied to cover images in all pages */
  pl?: PhotoPresentationLayout;
};

type ColorPaletteOverrides = {
  draft: EventDesignFragment;
  linkColor?: string;
  textColorHex?: string;
  backgroundColorHex?: string;
  pageColorHex?: string;
  pageFontColorHex?: string;
};

const defaultCropInfo = {
  x: 0,
  y: 6,
  width: 100,
  height: 84,
  unit: 'percent'
};

const getPhotoPresentation = (
  presentationLayout: PhotoPresentationLayout,
  cropInfo?: WebsitePagePhotoPresentationCropInfoFragment | null
): WebsitePagePhotoPresentationFragment => ({
  id: uuid(),
  presentationLayout,
  cropInfo: cropInfo ?? defaultCropInfo
});

const checkColorAndApply = (draft: EventDesignFragment, color: string, colorPaletteIndex: number) => {
  if (draft.colorPalette[colorPaletteIndex]) {
    draft.colorPalette[colorPaletteIndex].color.hex = color;
  } else {
    const [r, g, b] = chroma(color).rgb();
    draft.colorPalette[colorPaletteIndex] = {
      id: 'background',
      color: {
        hex: color,
        isLight: isLightColor(r, g, b)
      }
    };
  }
};

const applyColorPaletteOverrides = ({ draft, linkColor: linkColorHex, textColorHex, backgroundColorHex, pageColorHex, pageFontColorHex }: ColorPaletteOverrides) => {
  if (linkColorHex && draft.colorPalette[0]) {
    draft.colorPalette[0].color.hex = linkColorHex;
  }
  if (textColorHex && draft.colorPalette[1]) {
    draft.colorPalette[1].color.hex = textColorHex;
  }
  if (backgroundColorHex) {
    checkColorAndApply(draft, backgroundColorHex, 2);
  }
  if (pageColorHex) {
    checkColorAndApply(draft, pageColorHex, 3);
  }
  if (pageFontColorHex) {
    checkColorAndApply(draft, pageFontColorHex, 4);
  }
};

const applyThemeAndLayout = ({
  draft,
  selectedLayout,
  themeData,
  font,
  textColor,
  linkColor,
  backgroundColor,
  pageColor,
  pageFontColor,
  isCustomColorsEnabled,
  pageTransparency,
  pageShadow
}: ApplyThemeAndLayoutArgs) => {
  const textColorHex = parseColor(textColor)?.hex();
  const linkColorHex = parseColor(linkColor)?.hex();
  const backgroundColorHex = parseColor(backgroundColor)?.hex();
  const pageColorHex = parseColor(pageColor)?.hex();
  const pageFontColorHex = parseColor(pageFontColor)?.hex();

  if (selectedLayout && draft.websiteLayout && Object.values(DesignLayoutType).includes(selectedLayout)) {
    draft.websiteLayout.layoutType = selectedLayout;
  }
  if (themeData?.themeByThemeId) {
    draft.theme = themeData.themeByThemeId;
  }
  if (font) {
    draft.font.fontFamily = font;
  }
  if (isCustomColorsEnabled) {
    applyColorPaletteOverrides({
      draft,
      linkColor: linkColorHex,
      textColorHex,
      backgroundColorHex,
      pageColorHex,
      pageFontColorHex
    });
  }
  if (pageTransparency && draft.theme.websiteLayoutBrannanSettings?.brannanBackgroundTransparency) {
    const transparency = pageTransparency === 'true' ? 0.6 : 1;
    draft.theme.websiteLayoutBrannanSettings.brannanBackgroundTransparency = {
      ...draft.theme.websiteLayoutBrannanSettings.brannanBackgroundTransparency,
      rangeValue: transparency,
      supportsCustomization: false
    };
  }

  if (pageShadow) {
    const isPageShadowEnabled = pageShadow === 'true';

    draft.designPreferences.forEach(preference => {
      if (preference.featureType === DesignFeatureType.brannanInnerFrameBorder) {
        (preference as EventDesignPreferenceBooleanFragment).booleanValue = isPageShadowEnabled;
      }
    });
  }
  return draft;
};

const applyCoverPhotoOverrides = (draft: EventWebsiteGuestQuery, photoPresentationLayout?: PhotoPresentationLayout) => {
  if (photoPresentationLayout) {
    draft.eventByName?.pages.forEach(page => {
      if (photoPresentationLayout === 'center' || photoPresentationLayout === 'fullWidth') {
        page.photoPresentation = getPhotoPresentation(photoPresentationLayout, page.photoPresentation?.cropInfo);
      }
    });
  }
};

type UseLayoutAndThemeOverrideOptions = Readonly<{
  themeId?: string;
  layout?: DesignLayoutType;
  font?: string;
  ssr?: boolean;
}>;
/**
 * Custom hook to override layout and theme.
 *
 * @param {Object} options - Options for layout and theme override.
 * @param {boolean} [options.ssr=true] - Server-side rendering flag.
 *
 * @returns {Object} - Returns an object containing theme data, loading state, and functions to get event design and event with theme and layout applied.
 * @returns {Object} return.themeData - The theme data fetched by the query.
 * @returns {boolean} return.loadingThemeData - The loading state of the theme data query.
 * @returns {Function} return.getEventDesignWithThemeAndLayout - Function to get event design with theme and layout applied.
 * @returns {Function} return.getEventWithThemeAndLayout - Function to get event with theme and layout applied.
 */
export const useLayoutAndThemeOverride = ({ ssr = true }: UseLayoutAndThemeOverrideOptions) => {
  const {
    layout,
    theme: themeId,
    font,
    tc: textColor,
    bc: backgroundColor,
    lc: linkColor,
    pc: pageColor,
    pfc: pageFontColor,
    pt: pageTransparency,
    ps: pageShadow,
    pl: photoPresentationLayout
  }: QueryParamsValues = useQueryParams();
  const { data: themeData, loading: loadingThemeData } = useGetThemeByThemeIdQuery({
    variables: {
      themeId: (themeId as string) ?? ''
    },
    ssr,
    skip: !themeId,
    batchMode: 'fast'
  });
  const isCustomColorsEnabled = useFeatureValue('guestSiteColorsExperiment').value === 'treatment';

  const getEventDesignWithThemeAndLayout = useCallback(
    (eventDesign?: EventDesignFragment) => {
      return produce(eventDesign, draft => {
        if (draft) {
          applyThemeAndLayout({
            draft,
            selectedLayout: layout,
            themeData,
            font,
            textColor,
            linkColor,
            backgroundColor,
            pageColor,
            pageFontColor,
            isCustomColorsEnabled,
            pageTransparency,
            pageShadow
          });
        }
      });
    },
    [layout, themeData, font, textColor, linkColor, backgroundColor, pageColor, pageFontColor, isCustomColorsEnabled, pageTransparency, pageShadow]
  );

  const getEventWithThemeAndLayout = useCallback(
    (event?: EventWebsiteGuestQuery) => {
      return produce(event, draft => {
        if (draft?.eventByName?.eventDesign) {
          applyThemeAndLayout({
            draft: draft.eventByName.eventDesign,
            selectedLayout: layout,
            themeData,
            font,
            textColor,
            linkColor,
            backgroundColor,
            pageColor,
            pageFontColor,
            isCustomColorsEnabled,
            pageTransparency,
            pageShadow
          });
        }
        if (draft?.eventByName?.pages) {
          applyCoverPhotoOverrides(draft, photoPresentationLayout);
        }
      });
    },
    [layout, themeData, font, textColor, linkColor, backgroundColor, pageColor, pageFontColor, isCustomColorsEnabled, pageTransparency, pageShadow, photoPresentationLayout]
  );

  return {
    themeData,
    loadingThemeData,
    getEventDesignWithThemeAndLayout,
    getEventWithThemeAndLayout,
    layout,
    themeId,
    font,
    hasOverrides: layout || themeId || font || textColor || linkColor || backgroundColor || pageColor || pageFontColor || pageTransparency || pageShadow || photoPresentationLayout
  };
};
